import React, { useState } from "react";
import { Portal } from "../Portal/Portal";
import { apiClient } from "../../api/client";
import { HapticFeedback } from "../../utils/hapticFeedback";
import { useTheme } from "../../hooks/useTheme";
import styles from "./RatingModal.module.css";

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { theme } = useTheme();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = async (selectedRating: number) => {
    setRating(selectedRating);
    HapticFeedback.light();

    setTimeout(() => {
      if (selectedRating === 5) {
        setStep(3);
        saveRating(selectedRating, "");
      } else {
        setStep(2);
      }
    }, 300);
  };

  const saveRating = async (ratingValue: number, feedbackText: string) => {
    try {
      setIsSubmitting(true);

      await apiClient.submitRating(ratingValue, feedbackText);
      HapticFeedback.success();
    } catch (error) {
      HapticFeedback.error();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    await saveRating(rating, feedback);
    setTimeout(() => {
      onClose();
    }, 1000);
  };

  const handleExternalReview = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink("https://t.me/dpscontrol/5185");
    } else {
      window.open("https://t.me/dpscontrol/5185", "_blank");
    }
    onClose();
  };

  const handleClose = () => {
    HapticFeedback.light();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <div className={styles.overlay} onClick={handleClose}>
        <div
          className={`${styles.modal} ${
            theme === "dark" ? styles.darkTheme : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Кнопка закрытия */}
          <button onClick={handleClose} className={styles.closeButton}>
            ×
          </button>

          {/* Шаг 1: Выбор оценки */}
          {step === 1 && (
            <>
              <h2 className={styles.title}>Оцените приложение</h2>
              <p className={styles.subtitle}>
                Ваш отзыв поможет нам сделать
                <br />
                приложение лучше
              </p>

              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => handleStarClick(star)}
                    className={`${styles.star} ${
                      hoverRating >= star || rating >= star ? styles.active : ""
                    }`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Шаг 2: Форма обратной связи */}
          {step === 2 && (
            <>
              <div className={styles.ratedStars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`${styles.ratedStar} ${
                      star <= rating ? styles.active : ""
                    }`}
                  >
                    ★
                  </span>
                ))}
              </div>

              <h2 className={styles.title}>Оцените приложение</h2>
              <p className={styles.subtitle}>
                Ваш отзыв поможет нам сделать
                <br />
                приложение лучше
              </p>

              <div className={styles.textareaContainer}>
                <textarea
                  placeholder="Поделитесь мнением"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className={styles.textarea}
                  maxLength={300}
                />
                <div className={styles.counterBlock}>
                  <div className={styles.counter}>{feedback.length}/300</div>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={styles.submitButton}
              >
                {isSubmitting ? "Отправляем..." : "Отправить"}
              </button>
            </>
          )}

          {/* Шаг 3: Высокая оценка */}
          {step === 3 && (
            <>
              <div className={styles.ratedStars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`${styles.ratedStar} ${
                      star <= rating ? styles.active : ""
                    }`}
                  >
                    ★
                  </span>
                ))}
              </div>

              <h2 className={styles.title}>Спасибо за высокую оценку!</h2>
              <p className={styles.subtitle}>
                Хотите помочь нам и другим водителям? Оставьте короткий отзыв в
                Telegram-чате — за это вы получите баллы, которые скоро можно
                будет потратить в приложении
              </p>

              <button
                onClick={handleExternalReview}
                className={styles.submitButton}
              >
                Оставить отзыв
              </button>
            </>
          )}
        </div>
      </div>
    </Portal>
  );
};
